import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';
import FilterIcon from '@mui/icons-material/TuneOutlined';
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';

import Button from '@/components/Button';
import GenericDrawer from '@/components/GenericDrawer';
import { AgaveDataGrid } from '@/components/GenericTable/DataGridStyled';
import SearchInput from '@/components/SearchInput';

import Filters from './dependencies/Filters/Filters';
import DimensionsModal from './dependencies/modals/DimensionsModal';
import OrderDetailsModal from './dependencies/modals/OrderDetailsModal';
import { fulfillOrdersColumns } from './fulfill-orders-columns';
import { fulfillProductsColumns } from './fulfill-products-columns';
import useMLFulfill from './useMLFulfill';

const MLFulfill = () => {
  const hooks = useMLFulfill();
  const theme = useTheme();

  return (
    <Box display="flex" flexDirection="column" width="100%" gap="1.5rem">
      <Tabs
        value={hooks.activeTab}
        onChange={(_, val) => hooks.setActiveTab(val)}
        sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}
      >
        <Tab
          icon={<LocalShippingOutlinedIcon fontSize="small" />}
          iconPosition="start"
          label="Órdenes"
        />
        <Tab
          icon={<InventoryOutlinedIcon fontSize="small" />}
          iconPosition="start"
          label="Productos"
        />
        <Tab
          icon={<WarehouseOutlinedIcon fontSize="small" />}
          iconPosition="start"
          label="Almacenes"
        />
      </Tabs>

      {/* ── ORDERS TAB ── */}
      {hooks.activeTab === 0 && (
        <Paper
          elevation={3}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '1rem',
            padding: `${theme.spacing(4)} 0 0`,
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            width="100%"
            height="auto"
            sx={{
              padding: `0 ${theme.spacing(4)}`,
              flexDirection: { xxs: 'column', xl: 'row' },
              gap: { xxs: '1rem', xl: 0 },
              alignItems: 'center',
            }}
          >
            <SearchInput
              buttonText="Buscar"
              placeholder="Buscar por Orden, Paquete (Pack ID) o Envío de MercadoLibre"
              value={hooks.searchValue}
              onChange={(e) => hooks.handleSearchChange(e.target.value)}
              onClick={hooks.handleSearch}
              handleKeyPress={(e) => {
                if (e.key === 'Enter') hooks.handleSearch();
              }}
              sx={{ width: '100%' }}
            />

            <Box
              sx={{
                display: 'flex',
                flexDirection: { xxs: 'column', xs: 'row' },
                gap: '1rem',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Button variant="outlined" onClick={hooks.clearFilters} size="small">
                Borrar filtros
              </Button>
              <Box
                display="flex"
                gap="0.5rem"
                alignItems="center"
                justifyContent="center"
                onClick={() => hooks.setOpenFilters(true)}
                sx={{
                  cursor: 'pointer',
                  ':hover': { backgroundColor: theme.palette.primary.lighter },
                  padding: '0.5rem',
                  borderRadius: '1rem',
                }}
              >
                <FilterIcon color="primary" />
                <Typography variant="body1" fontWeight={theme.typography.fontWeightMedium}>
                  Filtros
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box width="100%" mt={2}>
            <Box sx={{ overflowX: 'auto', width: '100%' }}>
              <Box sx={{ minWidth: '700px' }}>
                <AgaveDataGrid
                  columns={fulfillOrdersColumns({
                    onCreateOrder: hooks.handleDirectCreate,
                    creatingOrderIds: hooks.creatingOrderIds,
                  })}
                  rows={hooks.orders.data}
                  getRowId={(row) => row.id}
                  rowCount={hooks.orders.total}
                  loading={hooks.orders.loading}
                  localeText={{ noRowsLabel: 'No hay órdenes disponibles' }}
                  disableRowSelectionOnClick
                  hideFooterSelectedRowCount
                  disableColumnMenu
                  paginationMode="server"
                  paginationModel={{
                    page: hooks.pagination.filters.page as number,
                    pageSize: hooks.pagination.filters.page_size as number,
                  }}
                  onPaginationModelChange={hooks.pagination.onPaginationModelChange}
                  onRowClick={({ row }) => hooks.handleOpenDetails(row)}
                  sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
                />
              </Box>
            </Box>
          </Box>
        </Paper>
      )}

      {/* ── PRODUCTS TAB ── */}
      {hooks.activeTab === 1 && (
        <Paper
          variant="outlined"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            gap: '1rem',
            padding: `${theme.spacing(4)} 0 0`,
          }}
        >
          <Box
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
            sx={{ padding: `0 ${theme.spacing(4)}` }}
          >
            {hooks.selectedProductIds.length > 0 && (
              <Button
                variant="contained"
                startIcon={<SyncOutlinedIcon />}
                disabled={hooks.syncingCatalog}
                onClick={hooks.handleSyncSelected}
              >
                {hooks.syncingCatalog
                  ? 'Sincronizando…'
                  : `Sincronizar seleccionados (${hooks.selectedProductIds.length})`}
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<SyncOutlinedIcon />}
              disabled={hooks.syncingCatalog}
              onClick={hooks.handleSyncCatalog}
            >
              {hooks.syncingCatalog ? 'Sincronizando…' : 'Sincronizar catálogo completo'}
            </Button>
          </Box>

          <Box sx={{ overflowX: 'auto', width: '100%' }}>
            <Box sx={{ minWidth: '700px' }}>
              <AgaveDataGrid
                columns={fulfillProductsColumns({
                  onSyncProduct: hooks.handleSyncProduct,
                  syncingIds: hooks.syncingIds,
                  onConvertToMe1: hooks.handleOpenConvertToMe1,
                  convertingMe1Ids: hooks.convertingMe1Ids,
                  isMe1AsTailorMadeEnabled: hooks.isMe1AsTailorMadeEnabled,
                })}
                rows={hooks.products.data}
                getRowId={(row) => row.id}
                rowCount={hooks.products.total}
                loading={hooks.products.loading}
                localeText={{ noRowsLabel: 'No hay productos disponibles' }}
                checkboxSelection
                disableRowSelectionOnClick
                isRowSelectable={(params) =>
                  params.row.status === 'active' && params.row.shipping?.mode !== 'me2'
                }
                rowSelectionModel={{ ids: new Set(hooks.selectedProductIds), type: 'include' }}
                onRowSelectionModelChange={(selectionModel) =>
                  hooks.setSelectedProductIds(Array.from(selectionModel.ids) as string[])
                }
                getRowClassName={(params) =>
                  params.row.status !== 'active' || params.row.shipping?.mode === 'me2'
                    ? 'row-inactive'
                    : ''
                }
                hideFooterSelectedRowCount
                disableColumnMenu
                paginationMode="server"
                paginationModel={{
                  page: hooks.productPagination.filters.page as number,
                  pageSize: hooks.productPagination.filters.page_size as number,
                }}
                onPaginationModelChange={hooks.productPagination.onPaginationModelChange}
                sx={{
                  '& .row-inactive': {
                    opacity: 0.5,
                    cursor: 'default',
                    '& .MuiCheckbox-root': { pointerEvents: 'none' },
                    '& .MuiButtonBase-root': { pointerEvents: 'none' },
                  },
                }}
              />
            </Box>
          </Box>
        </Paper>
      )}

      {/* ── WAREHOUSES TAB ── */}
      {hooks.activeTab === 2 && (
        <Paper
          variant="outlined"
          sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: '1.5rem', p: 3 }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            Almacenes disponibles
          </Typography>

          {hooks.warehouses.loading ? (
            <Box display="flex" alignItems="center" gap={2}>
              <CircularProgress size={20} />
              <Typography>Cargando almacenes…</Typography>
            </Box>
          ) : (
            <Box display="flex" flexDirection="column" gap={1}>
              {hooks.warehouses.data.map((w) => (
                <Box
                  key={w.warehouseId}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}
                >
                  <Box>
                    <Typography variant="body1" fontWeight={500}>
                      {w.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {w.coordinates.address}
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={hooks.activeWarehouseIds.includes(w.warehouseId)}
                        onChange={() => hooks.handleToggleWarehouse(w.warehouseId)}
                      />
                    }
                    label="Activo"
                  />
                </Box>
              ))}
            </Box>
          )}

          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              disabled={hooks.warehouses.saving || hooks.warehouses.loading}
              onClick={hooks.handleSaveWarehouses}
            >
              {hooks.warehouses.saving ? 'Guardando…' : 'Guardar cambios'}
            </Button>
          </Box>
        </Paper>
      )}

      <GenericDrawer
        open={hooks.openFilters}
        onClose={hooks.onClose}
        title="Filtros"
        disableEscapeKeyDown={true}
        iconTitle={<FilterIcon color="primary" />}
      >
        <Filters
          selectedFilters={hooks.filters}
          onChange={hooks.onChangeFilter}
          clearFilters={hooks.clearFilters}
          applyFilters={hooks.applyFilters}
        />
      </GenericDrawer>

      <OrderDetailsModal
        isOpen={hooks.modals.details}
        onClose={hooks.handleCloseDetails}
        order={hooks.selectedOrder}
      />

      <DimensionsModal
        key={hooks.dimensionsModal.open ? (hooks.dimensionsModal.productId ?? 'open') : 'closed'}
        isOpen={hooks.dimensionsModal.open}
        onClose={hooks.handleCloseDimensionsModal}
        onConfirm={hooks.handleConfirmDimensions}
        initialDimensions={hooks.dimensionsModal.initialDimensions}
        loading={
          hooks.dimensionsModal.productId
            ? hooks.dimensionsModal.actionType === 'me1'
              ? hooks.convertingMe1Ids.has(hooks.dimensionsModal.productId)
              : hooks.syncingIds.has(hooks.dimensionsModal.productId)
            : false
        }
        title={
          hooks.dimensionsModal.actionType === 'me1'
            ? 'Convertir publicación a ME1'
            : 'Dimensiones del producto'
        }
        description={
          hooks.dimensionsModal.actionType === 'me1'
            ? 'Ingresa o confirma las dimensiones y peso requeridos para convertir la publicación a Mercado Envíos 1 (ME1).'
            : undefined
        }
        confirmText={
          hooks.dimensionsModal.actionType === 'me1'
            ? 'Confirmar y convertir'
            : 'Confirmar y sincronizar'
        }
        loadingText={
          hooks.dimensionsModal.actionType === 'me1' ? 'Convirtiendo…' : 'Sincronizando…'
        }
      />
    </Box>
  );
};

export default MLFulfill;
